// src/hooks/useRecaptcha.js
import { useRef, useEffect, useCallback } from 'react';

/**
 * A custom hook to manage the reCAPTCHA lifecycle, including execution and cleanup.
 * This helps prevent timeout errors when navigating between pages with reCAPTCHA.
 * @param {function} onVerify - The async callback to execute with the reCAPTCHA token.
 * @param {function} [onExpire] - Optional callback for when the reCAPTCHA expires or is closed.
 * @param {function} [onError] - Optional callback for when the reCAPTCHA script fails to load.
 * @returns {object} - { recaptchaRef, execute, onChange, reset, onErrored }
 */
export const useRecaptcha = (onVerify, onExpire, onError) => {
  const recaptchaRef = useRef(null);

  useEffect(() => {
    return () => {
      // Intentionally empty cleanup. Calling reset() during unmount causes a race condition
      // with the reCAPTCHA script, leading to unhandled promise rejections.
      // The library is expected to handle its own cleanup when its container is removed from the DOM.
    };
  }, []); // Empty dependency array ensures this runs only on mount and unmount.

  // The function the component will call to start the reCAPTCHA challenge.
  const execute = useCallback(() => {
    if (recaptchaRef.current) {
      recaptchaRef.current.execute();
    }
  }, []);

  // Function to manually reset the reCAPTCHA widget.
  const reset = useCallback(() => {
    if (recaptchaRef.current) {
      try {
        recaptchaRef.current.reset();
        console.log('[useRecaptcha] reCAPTCHA has been reset.');
      } catch (e) {
        console.warn('[useRecaptcha] Could not reset reCAPTCHA, it might have already been removed.', e);
      }
    }
  }, []);

  // The internal onErrored handler for the ReCAPTCHA component.
  const onErrored = useCallback(() => {
    console.error('[useRecaptcha] reCAPTCHA script failed to load. This can be caused by network issues or ad-blockers.');
    if (onError) {
      onError();
    }
  }, [onError]);

  // The internal onChange handler for the ReCAPTCHA component.
  const onChange = useCallback(
    async (token) => {
      //      console.log('[useRecaptcha] onChange triggered. Token:', token); // Log the token
      if (token) {
        try {
          await onVerify(token);
        } catch (error) {
          // This catch block is crucial. It prevents unhandled promise rejections
          // if the onVerify function (e.g., the login handler) throws an error.
          console.error('[useRecaptcha] An error occurred during the onVerify callback:', error);
        }
      } else {
        // Token is null or undefined, which means the reCAPTCHA expired or was closed by the user.
        console.log('[useRecaptcha] reCAPTCHA token is null or expired.');
        if (onExpire) {
          onExpire();
        }
      }
    },
    [onVerify, onExpire] // onError is not needed here as onErrored has its own useCallback
  );

  // The internal onExpired handler for the ReCAPTCHA component.
  const onExpiredInternal = useCallback(() => {
    console.log('[useRecaptcha] reCAPTCHA token expired.');
    if (onExpire) {
      onExpire();
    }
    // Explicitly reset the widget when it expires to ensure it's ready for the next use.
    reset();
  }, [onExpire, reset]);

  return { recaptchaRef, execute, onChange, reset, onErrored, onExpired: onExpiredInternal };
};
